import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, UnauthorizedError } from './api';

/**
 * Espelha `AgentRole` do Prisma: ADMIN | SUPERVISOR | AGENT.
 *
 * Eu havia escrito 'MANAGER' — papel que não existe no banco. O efeito era
 * silencioso e sério: `PESO['SUPERVISOR']` era `undefined`, toda comparação
 * `undefined >= 1` dava falso, e um supervisor real ficava sem Métricas, sem
 * excluir contato e sem cancelar disparo. Nada quebrava; a permissão
 * simplesmente não existia.
 */
export type Role = 'AGENT' | 'SUPERVISOR' | 'ADMIN';

export interface Sessao {
  agent: { id: string; name: string; email: string; role: Role };
  tenant: { id: string; name: string; slug: string };
  stats: { contacts: number; conversations: number; messages: number };
}

/** Identidade da plataforma (Setter), não de um workspace. Não tem papel nem
 *  tenant: um master não pertence a empresa nenhuma, ele as administra. */
export interface Master { id: string; name: string; email: string }

type Estado =
  | { status: 'carregando' }
  | { status: 'autenticado'; sessao: Sessao }
  | { status: 'master'; master: Master }
  | { status: 'anonimo' }
  | { status: 'erro'; mensagem: string };

interface Valor { estado: Estado; revalidar: () => void }

const Ctx = createContext<Valor>({ estado: { status: 'carregando' }, revalidar: () => {} });

export function useSessao(): Sessao {
  const { estado } = useContext(Ctx);
  if (estado.status !== 'autenticado') {
    throw new Error('useSessao só pode ser chamado dentro da área autenticada.');
  }
  return estado.sessao;
}

export function useEstadoSessao(): Estado {
  return useContext(Ctx).estado;
}

/** Reconsulta /auth/me. Usado depois do login — recarregar a página inteira
 *  funcionaria, mas descarta o bundle já carregado e pisca a tela. */
export function useRevalidarSessao(): () => void {
  return useContext(Ctx).revalidar;
}

/**
 * `GET /api/auth/me` é a primeira chamada do produto e decide tudo o que vem
 * depois: papel, nome do workspace, contadores da visão geral.
 *
 * Anônimo NÃO é erro. O front anterior tratava 401 no mesmo caminho de falha de
 * rede, então uma sessão expirada aparecia como "erro ao carregar" em vez de
 * levar para o login.
 *
 * São DUAS identidades distintas no mesmo cookie, não uma com mais permissão:
 * a de workspace (`/auth/me`, tem tenant e papel) e a da plataforma
 * (`/auth/master/me`, não tem nenhum dos dois). `/auth/me` responde 401 para um
 * cookie master — corretamente, porque não existe agente ali. Por isso o 401
 * não conclui "anônimo" antes de perguntar pela segunda: enquanto concluía,
 * quem entrava como master caía de volta no formulário de login, sem mensagem
 * nenhuma, num laço.
 */
export function SessaoProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ status: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    api<Sessao>('/auth/me')
      .then((sessao) => vivo && setEstado({ status: 'autenticado', sessao }))
      .catch((error: unknown) => {
        if (!vivo) return Promise.resolve();
        if (!(error instanceof UnauthorizedError)) {
          setEstado({ status: 'erro', mensagem: 'Não foi possível verificar sua sessão.' });
          return Promise.resolve();
        }
        return api<{ user: Master }>('/auth/master/me')
          .then(({ user }) => { if (vivo) setEstado({ status: 'master', master: user }); })
          .catch((falha: unknown) => {
            if (!vivo) return;
            if (falha instanceof UnauthorizedError) setEstado({ status: 'anonimo' });
            else setEstado({ status: 'erro', mensagem: 'Não foi possível verificar sua sessão.' });
          });
      });
    return () => { vivo = false; };
  }, [tentativa]);

  const valor = useMemo<Valor>(() => ({ estado, revalidar: () => setTentativa((n) => n + 1) }), [estado]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/** Hierarquia de papel em um lugar só. Espalhada, cada tela inventava a sua
 *  — o front anterior tinha `role === 'ADMIN'` em umas telas e
 *  `role !== 'AGENT'` em outras para a MESMA permissão. */
const PESO: Record<Role, number> = { AGENT: 0, SUPERVISOR: 1, ADMIN: 2 };

export function podeAtuar(role: Role, minimo: Role): boolean {
  return PESO[role] >= PESO[minimo];
}
